import { msg } from '@lingui/core/macro';
import { Form, Formik } from 'formik';
import _ from 'lodash';
import { Notification, NotificationType } from 'modules/core/domain/interfaces/supabase/notifications/types';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useState } from 'react';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { usePreventNavigation } from 'shared/hooks';
import UDFormInput from 'ud-form/components/input';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDFormSelect from 'ud-form/components/select';
import UDButton from 'ud-ui/components/button';
import * as Yup from 'yup';

import NotificationPreview from './notification-preview';

// Форма видна только super_admin и остаётся русской (границы перевода), поэтому опции — строки, не `msg`.
const typeOptions = [
  { value: 'all', label: 'Все типы' },
  { value: 'update', label: 'Обновление' },
  { value: 'bugfix', label: 'Исправления ошибок' },
  { value: 'maintenance', label: 'Тех. работы' },
  { value: 'info', label: 'Инфо' },
  { value: 'upcoming', label: 'Будущие обновления' },
  { value: 'known_issue', label: 'Известная проблема' },
  { value: 'user_event', label: 'Личные события' },
];

type TypeOption = (typeof typeOptions)[number];

export interface FormValues {
  type: TypeOption | null;
  title: string;
  text: string;
  userId: number | null | undefined;
}

const validationSchema = Yup.object().shape({
  type: Yup.object().required('Выберите тип уведомления'),
  title: Yup.string().required('Введите заголовок').trim(),
  text: Yup.string().required('Введите текст').trim(),
  userId: Yup.number().when('type', {
    is: (type: TypeOption | null) => type?.value === 'user_event',
    then: (schema) => schema.required('Для события пользователя необходимо указать ID'),
    otherwise: (schema) => schema.nullable().optional(),
  }),
});

interface Props {
  initialValues?: Partial<Notification>;
  onSubmit: (values: Partial<Notification>) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
}

const NotificationForm: React.FC<Props> = ({ initialValues, onSubmit, onCancel, loading = false }) => {
  const toastApiError = useToastApiError();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    try {
      const trimmedValues = {
        ...values,
        type: values.type?.value as NotificationType,
        title: values.title?.trim(),
        text: values.text?.trim(),
      };
      await onSubmit(trimmedValues);
    } catch (error) {
      toastApiError(error, msg`Не удалось сохранить уведомление`);
    }
  };

  const initialType = initialValues?.type ? (typeOptions.find((opt) => opt.value === initialValues.type) ?? null) : null;

  let initialValues1 = {
    type: initialType ?? typeOptions.find((opt) => opt.value === 'info') ?? null,
    title: initialValues?.title ?? '',
    text: initialValues?.text ?? '',
    userId: initialValues?.userId,
  };

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TEAMS.INDEX,
  });

  return (
    <Formik
      initialValues={initialValues1}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, formikHelpers) => {
        // Текущие значения — база сразу; `isSubmitting: true` переживает сброс — кнопку держит промис запроса
        // (раньше замка не было вовсе: сабмит не возвращал промис и тут же сбрасывал форму).
        formikHelpers.resetForm({ values, isSubmitting: true });
        setIsFormDirty(false);
        return handleSubmit(values);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {({ isSubmitting, values, errors, touched }) => {
        const previewValues = {
          ...values,
          type: values.type?.value as NotificationType,
        };

        const showUserIdField = values.type?.value === 'user_event';

        return (
          <>
            <Form className="flex w-1/2 flex-col gap-4">
              <UDFormSelect
                name="type"
                label="Тип уведомления"
                required
                options={typeOptions.filter((option) => option.value !== 'all')}
                errorVisible={!!(touched.type && errors.type)}
              />

              <UDFormInput
                name="title"
                label="Заголовок"
                required
                placeholder="Введите заголовок уведомления"
                errorVisible={!!(touched.title && errors.title)}
              />

              <UDFormTextarea
                name="text"
                label="Текст"
                required
                placeholder="Введите текст уведомления"
                rows={5}
                style={{ resize: 'vertical', minHeight: '120px' }}
                errorVisible={!!(touched.text && errors.text)}
              />

              {showUserIdField && (
                <UDFormInput
                  name="userId"
                  label="ID пользователя"
                  type="number"
                  required
                  placeholder="Введите ID пользователя"
                  errorVisible={!!(touched.userId && errors.userId)}
                />
              )}

              <div className="mt-4 flex justify-start gap-3">
                {/* `loading` — загрузка уведомления на странице правки, запрос идёт не отсюда; свой сабмит запирает Formik. */}
                <UDButton variant="primary" type="submit" loading={loading} className="w-full">
                  {initialValues ? 'Сохранить' : 'Создать'}
                </UDButton>
                {onCancel && (
                  <UDButton
                    variant="secondary"
                    onClick={onCancel ?? backHandler}
                    disabled={isSubmitting || loading}
                    loading={false}
                    className="w-full"
                  >
                    Отмена
                  </UDButton>
                )}
              </div>
            </Form>

            <NotificationPreview notification={previewValues} />
            <PreventNavigationModal />
          </>
        );
      }}
    </Formik>
  );
};

export default NotificationForm;
